import {isFeatureEnabled} from 'feature-flags';
import {Box, HStack, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useSelector} from 'react-redux';

import {isNil, isNotNil, slice} from 'ramda';

import {
  selectTenant,
  useGetAuctionServerListQuery,
  useSourcingGetSourcingServersQuery,
} from '@dms/api/core';
import {selectCurrentUserInfo} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import {i18n} from '@dms/i18n';
import {SkeletonLoadingBox, useAddress} from '@dms/shared';

import {suffixTestId, TestIdProps, useToggle} from 'shared/web';

import {KwToPsMultiplier} from '../../../../constants/KwToPsMultiplier';
import {sourcingFilterConstants} from '../../../../constants/sourcingFilterConstants';
import {UNBOUNDED_PRICE} from '../../../../constants/UNBOUNDED_PRICE';
import {useApiEnums} from '../../../../hooks/useApiEnums';
import {useSourcingType} from '../../../../hooks/useSourcingType';
import {getAuctionTypePresentation} from '../../../../utils/getAuctionTypePresentation';
import {useMakeModelFilter} from '../../hooks/useMakeModelFilter';
import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {useTrimLevelOptions} from '../../hooks/useTrimLevelOptions';
import {CarStatusFilter} from '../../types/CarStatusFilter';
import {VehicleCategoryFilter} from '../../types/VehicleCategoryFilter';
import {areVanDimensionFiltersVisible} from '../../utils/areVanDimensionFiltersVisible';
import {getAppliedVehicleCategory} from '../../utils/getAppliedVehicleCategory';
import {isOriginCountryFilterVisible} from '../../utils/isOriginCountryFilterVisible';
import {isSuspiciousReasonFilterEnabled} from '../../utils/isSuspiciousReasonFilterEnabled';
import {CollapsibleChipRows} from '../CollapsibleChipRows/CollapsibleChipRows';
import {FilterFieldName} from '../FilterEditor/utils/getFilterSections';
import {useFilters} from '../FiltersContext/FiltersContext';
import {EditableFilterTag} from './components/EditableFilterTag';
import {FilterTagButtons} from './components/FilterTagButtons';
import {useGetBooleanBasedTags} from './hooks/useGetBooleanBasedTags';
import {useGetNumberBasedTags} from './hooks/useGetNumberBasedTags';
import {useGetStringBasedTags} from './hooks/useGetStringBasedTags';

const NUMBER_OF_VISIBLE_TAGS = 8;
const TAG_ROW_LIMIT = 2;

interface FilterTagsProps extends TestIdProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  showSaveButton?: boolean;
  onEdit?: (field: FilterFieldName) => void;
}

// The class alone ("L2", "H1") is ambiguous as a chip, so it is prefixed with the filter name.
// A key missing from the codelist keeps its empty label instead of rendering "Length: undefined".
const withFilterLabel = <T extends {label: string | undefined}>(filterLabel: string, tags: T[]) =>
  tags.map((tag) => ({
    ...tag,
    label: isNil(tag.label) ? tag.label : `${filterLabel}: ${tag.label}`,
  }));

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function FilterTags(props: FilterTagsProps) {
  const [isExpanded, toggleIsExpanded] = useToggle(false);
  const {filters, onUpdateFilters} = useFilters();
  const sourcingType = useSourcingType();
  const {options: trimLevelOptions} = useTrimLevelOptions(Boolean(filters.trimLevels?.length));
  const {
    selectedMakes,
    selectedModelFamilies,
    selectedModels,
    getMake,
    getModel,
    getModelFamily,
    unselectMake,
    unselectModel,
    unselectModelFamily,
    isLoading: areMakeModelsLoading,
  } = useMakeModelFilter();
  const {data: {country: countryCode} = {}} = useSelector(selectTenant);
  const {data: sellerServers} = useSourcingGetSourcingServersQuery(
    {countryCode: countryCode!},
    {skip: isNil(countryCode)}
  );
  const {data: auctionServers} = useGetAuctionServerListQuery();
  const selectedCurrency = useSelector(selectCurrentUserInfo)?.data?.settings.currency;
  const currencySuffix = isNotNil(selectedCurrency) ? ` ${selectedCurrency}` : '';
  const {composeAddress} = useAddress();
  const {getEnum, isLoading: areEnumsLoading} = useApiEnums({flat: true});

  const getStringBasedTags = useGetStringBasedTags();
  const getNumberBasedTags = useGetNumberBasedTags();
  const getBooleanBasedTags = useGetBooleanBasedTags();

  const sellerServerTags =
    filters.sellerServer?.map((sellerServer) => ({
      filterName: 'sellerServer' as const,
      label: sellerServers?.find(({code}) => code === sellerServer)?.name ?? sellerServer,
      onDelete: () =>
        onUpdateFilters(
          'sellerServer',
          filters.sellerServer?.filter((item) => sellerServer !== item)
        ),
    })) ?? [];

  const auctionServerTags =
    filters.auctionServer?.map((auctionServerId) => ({
      filterName: 'auctionServer' as const,
      label: auctionServers?.find(({id}) => id === auctionServerId)?.name ?? auctionServerId,
      onDelete: () =>
        onUpdateFilters(
          'auctionServer',
          filters.auctionServer?.filter((item) => auctionServerId !== item)
        ),
    })) ?? [];

  const auctionTypeTags =
    filters.auctionType?.map((auctionType) => ({
      filterName: 'auctionType' as const,
      label: getAuctionTypePresentation(auctionType)?.label ?? auctionType,
      onDelete: () =>
        onUpdateFilters(
          'auctionType',
          filters.auctionType?.filter((item) => auctionType !== item)
        ),
    })) ?? [];

  const carStatusTags =
    filters.carStatus?.map((carStatus) => ({
      filterName: 'carStatus' as const,
      label: match(carStatus)
        .with(CarStatusFilter.ACTIVE, () => i18n.t('general.labels.active'))
        .with(CarStatusFilter.ENDED, () => i18n.t('entity.auction.labels.ended'))
        .otherwise(() => carStatus),
      onDelete: () =>
        onUpdateFilters(
          'carStatus',
          filters.carStatus?.filter((item) => carStatus !== item)
        ),
    })) ?? [];

  const suspiciousFilterTags =
    isFeatureEnabled(featureFlags.SOURCING_SUSPICIOUS_VEHICLES) &&
    (filters.hideSuspicious ||
      (isSuspiciousReasonFilterEnabled() && Boolean(filters.excludeSuspiciousReasons?.length)))
      ? [
          {
            filterName: 'hideSuspicious' as const,
            label: i18n.t('entity.sourcing.filters.labels.hideSuspicious'),
            onDelete: () => {
              onUpdateFilters('hideSuspicious', false);
              if (isSuspiciousReasonFilterEnabled()) {
                onUpdateFilters('excludeSuspiciousReasons', undefined);
              }
            },
          },
        ]
      : [];

  const address = {
    street: filters.address?.street ?? '',
    descriptiveNumber: filters.address?.descriptiveNumber ?? null,
    orientationNumber: filters.address?.orientationNumber ?? null,
    city: filters.address?.city ?? '',
    zip: filters.address?.zipCode ?? '',
    addressComplement: null,
    country: filters.address?.country ?? '',
    prefix: filters.address?.prefix ?? null,
    district: null,
    state: filters.address?.state ?? null,
  };

  const locationTags =
    filters.address && filters.radius
      ? [
          {
            filterName: 'address' as const,
            label: `${composeAddress(address)} (${filters.radius} km)`,
            onDelete: () => {
              onUpdateFilters('address', null);
              onUpdateFilters('radius', null);
            },
          },
        ]
      : [];

  const isVanFiltersEnabled = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS);
  const appliedVehicleCategory = getAppliedVehicleCategory(filters.vehicleCategory, sourcingType);
  const vehicleCategoryTags =
    isVanFiltersEnabled && isNotNil(appliedVehicleCategory)
      ? [
          {
            filterName: 'vehicleCategory' as const,
            label: match(appliedVehicleCategory)
              .with(VehicleCategoryFilter.CAR, () =>
                i18n.t('entity.sourcing.filters.labels.vehicleCategoryCar')
              )
              .with(VehicleCategoryFilter.VAN, () =>
                i18n.t('entity.sourcing.filters.labels.vehicleCategoryVan')
              )
              .exhaustive(),
            onDelete: () => onUpdateFilters('vehicleCategory', null),
          },
        ]
      : [];

  const isVanDimensionFilterVisible = areVanDimensionFiltersVisible(
    filters.vehicleCategory,
    sourcingType,
    isVanFiltersEnabled
  );
  const vanDimensionTags = isVanDimensionFilterVisible
    ? [
        ...withFilterLabel(
          i18n.t('entity.vehicle.labels.vehicleLength'),
          getStringBasedTags('vehicleLengthCategory', 'dimensions_length')
        ),
        ...withFilterLabel(
          i18n.t('entity.vehicle.labels.vehicleHeight'),
          getStringBasedTags('vehicleHeightCategory', 'dimensions_height')
        ),
      ]
    : [];

  const makeTags = selectedMakes.map((item) => ({
    filterName: 'make' as const,
    label: getMake(item)?.label,
    onDelete: () => unselectMake(item),
  }));

  const modelFamilyTags = selectedModelFamilies.map((item) => ({
    filterName: 'model' as const,
    label: getModelFamily(item)?.label,
    onDelete: () => unselectModelFamily(item),
  }));

  const modelTags = selectedModels.map((item) => ({
    filterName: 'model' as const,
    label: getModel(item)?.label,
    onDelete: () => unselectModel(item),
  }));

  const adTitleTags = filters.adTitle
    ? [
        {
          filterName: 'adTitle' as const,
          label: filters.adTitle,
          onDelete: () => onUpdateFilters('adTitle', null),
        },
      ]
    : [];

  const featureTiersTags =
    filters.trimLevels?.map((trimLevel) => ({
      filterName: 'trimLevels' as const,
      label: trimLevelOptions.find((option) => option.value === trimLevel)?.label ?? trimLevel,
      onDelete: () =>
        onUpdateFilters(
          'trimLevels',
          filters.trimLevels?.filter((item) => trimLevel !== item)
        ),
    })) ?? [];

  const colorTags =
    filters.selectedColors?.map((color) => ({
      filterName: 'selectedColors' as const,
      label: color.label,
      onDelete: () =>
        onUpdateFilters(
          'selectedColors',
          filters.selectedColors?.filter((item) => color.key !== item.key)
        ),
    })) ?? [];

  const doorCountTags =
    filters.doorCount?.map((doorCount) => ({
      filterName: 'doorCount' as const,
      label: match(doorCount)
        .with('DOORCOUNT_1', () => i18n.t('entity.vehicle.labels.doors', {count: 1}))
        .with('DOORCOUNT_2_3', () => `2/${i18n.t('entity.vehicle.labels.doors', {count: 3})}`)
        .with('DOORCOUNT_4_5', () => `4/${i18n.t('entity.vehicle.labels.doors', {count: 5})}`)
        .with('DOORCOUNT_6_7', () => `6/${i18n.t('entity.vehicle.labels.doors', {count: 7})}`)
        .otherwise(
          () =>
            `${i18n.t('entity.vehicle.labels.numberOfDoors')}: ${getEnum('door_count').find(({key}) => key === doorCount)?.label}`
        ),
      onDelete: () =>
        onUpdateFilters(
          'doorCount',
          filters.doorCount?.filter((item) => item !== doorCount)
        ),
    })) ?? [];

  const tags = [
    ...(isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN)
      ? withFilterLabel(
          i18n.t('entity.vehicle.labels.sellerCountry'),
          getStringBasedTags('sellerCountry', 'country')
        )
      : getStringBasedTags('sellerCountry', 'country')),
    // Prefixed so that the same country picked as seller and origin country gives two telling chips.
    ...(isOriginCountryFilterVisible(
      sourcingType,
      isFeatureEnabled(featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER)
    )
      ? withFilterLabel(
          i18n.t('entity.vehicle.labels.countryOfOrigin'),
          getStringBasedTags('originCountry', 'country')
        )
      : []),
    ...sellerServerTags,
    ...getStringBasedTags('sellerType', 'seller_type'),
    ...auctionTypeTags,
    ...carStatusTags,
    ...auctionServerTags,
    ...getNumberBasedTags(
      'avgNumberAds',
      i18n.t('entity.warehouse.labels.averageCarsInStock'),
      sourcingFilterConstants.AVG_NUMBER_ADS_MAX
    ),
    ...locationTags,
    ...getNumberBasedTags(
      'priceCZK',
      i18n.t('general.labels.price'),
      filters.priceRangeVersion === 2 ? UNBOUNDED_PRICE : sourcingFilterConstants.PRICE_MAX,
      isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN) ? currencySuffix : undefined
    ),
    ...(filters.isMarginAbsolute
      ? getNumberBasedTags(
          'margin',
          i18n.t('entity.vehicle.labels.margin'),
          sourcingFilterConstants.MARGIN_LIMIT_MAX,
          currencySuffix,
          {
            onDelete: () => {
              onUpdateFilters('margin', undefined);
              onUpdateFilters('marginPercent', undefined);
            },
          }
        )
      : getNumberBasedTags(
          'marginPercent',
          i18n.t('entity.vehicle.labels.margin'),
          sourcingFilterConstants.MARGIN_PERCENT_LIMIT_MAX,
          '%',
          {
            onDelete: () => {
              onUpdateFilters('margin', undefined);
              onUpdateFilters('marginPercent', undefined);
            },
          }
        )),
    ...getNumberBasedTags(
      'daysOnDisplay',
      i18n.t('entity.vehicle.labels.daysOnDisplay'),
      sourcingFilterConstants.DAYS_ON_DISPLAY
    ),
    ...getNumberBasedTags(
      'liquidity',
      i18n.t('entity.vehicle.labels.liquidity'),
      sourcingFilterConstants.LIQUIDITY_MAX
    ),
    ...getNumberBasedTags('pricePosition', i18n.t('entity.vehicle.labels.pricePosition'), 100, '%'),
    ...getBooleanBasedTags('reclaimableVat', i18n.t('general.labels.reclaimableVAT')),
    ...suspiciousFilterTags,
    ...getBooleanBasedTags('hitlistOnly', i18n.t('entity.sourcing.filters.labels.hitlistOnly')),
    ...getBooleanBasedTags('hideBlacklist', i18n.t('entity.sourcing.filters.labels.hideBlacklist')),
    ...vehicleCategoryTags,
    ...modelTags,
    ...modelFamilyTags,
    ...makeTags,
    ...getStringBasedTags('fuelType', 'fuel_type'),
    ...getStringBasedTags('transmission', 'transmission'),
    ...getStringBasedTags('bodyType', 'car_style'),
    ...vanDimensionTags,
    ...doorCountTags,
    ...getStringBasedTags('drive', 'drive'),
    ...(isFeatureEnabled(featureFlags.SOURCING_INTERIOR_MATERIAL_FILTER)
      ? getStringBasedTags('interiorMaterial', 'interior_material')
      : []),
    ...featureTiersTags,
    ...adTitleTags,
    ...getNumberBasedTags(
      'mileageRange',
      i18n.t('entity.vehicle.labels.mileageRange'),
      sourcingFilterConstants.MILEAGE_RANGE_MAX
    ),
    ...getNumberBasedTags('year', i18n.t('entity.vehicle.labels.year')),
    ...getNumberBasedTags(
      'power',
      i18n.t('entity.vehicle.labels.power2'),
      sourcingFilterConstants.POWER_LIMIT_MAX,
      filters.isPowerInKW ? ' kW' : ' PS',
      {
        formatter: (number) =>
          (filters.isPowerInKW ? number : (number ?? 0) * KwToPsMultiplier)?.toFixed(0) ?? null,
      }
    ),
    ...getNumberBasedTags(
      'cubicCapacity',
      i18n.t('entity.vehicle.labels.cubicCapacity'),
      sourcingFilterConstants.CUBIC_CAPACITY_LIMITS[1]
    ),
    ...getNumberBasedTags(
      'numberOfSeats',
      i18n.t('entity.vehicle.labels.numberOfSeats'),
      sourcingFilterConstants.NUMBER_OF_SEATS_MAX
    ),
    ...getNumberBasedTags(
      'featureScore',
      i18n.t('entity.vehicle.labels.rankEquipment'),
      sourcingFilterConstants.FEATURE_SCORE_MAX,
      '%'
    ),
    ...colorTags,
    ...getStringBasedTags('features', 'feature'),
  ];

  const mappedTags = tags.map(({label, onDelete, filterName}, index) => (
    <EditableFilterTag
      key={label ? `tag-${label}-${index}` : `tag-${index}`}
      onDelete={onDelete}
      onClick={props.onEdit ? () => props.onEdit?.(filterName) : undefined}
      label={label ?? ''}
      data-testid={suffixTestId(`tag-${label?.replace(' ', '_')}`, props)}
    />
  ));
  const isTagsLoading =
    props.filterPresetsControl.isLoading || areEnumsLoading || areMakeModelsLoading;

  if (isFeatureEnabled(featureFlags.SOURCING_COLLAPSIBLE_FILTER_HEADER)) {
    return (
      <HStack width="100%">
        <CollapsibleChipRows
          rowLimit={TAG_ROW_LIMIT}
          data-testid={suffixTestId('chipRows', props)}
          spacing={1}
          isExpanded={isExpanded}
          // A single placeholder chip while loading keeps the trailing buttons mounted.
          items={
            isTagsLoading
              ? [
                  {
                    id: 'tags-loading-placeholder',
                    node: (
                      <SkeletonLoadingBox isLoading width={22} height={6} borderRadius="small" />
                    ),
                  },
                ]
              : tags.map(({label, onDelete, filterName}, index) => ({
                  id: `${label}-${index}`,
                  node: (
                    <EditableFilterTag
                      onDelete={onDelete}
                      onClick={props.onEdit ? () => props.onEdit?.(filterName) : undefined}
                      label={label ?? ''}
                      data-testid={suffixTestId(`tag-${label?.replace(' ', '_')}`, props)}
                    />
                  ),
                }))
          }
          renderTrailing={(hiddenCount) => (
            // Chips sit tight (spacing 1); the buttons get the same gap as next to the presets.
            <Box paddingLeft={1}>
              <FilterTagButtons
                tagCount={tags.length}
                collapsedTagCount={hiddenCount}
                filterPresetsControl={props.filterPresetsControl}
                isExpanded={isExpanded}
                showSaveButton={props.showSaveButton}
                toggleIsExpanded={toggleIsExpanded}
                data-testid={suffixTestId('filterTagButtons', props)}
              />
            </Box>
          )}
        />
      </HStack>
    );
  }

  return (
    <HStack width="100%" align="center" spacing={1} wrap>
      <SkeletonLoadingBox
        isLoading={isTagsLoading}
        width={22}
        height={6}
        borderRadius="small"
        contentStyles={{display: 'contents'}}
      >
        {tags.length <= NUMBER_OF_VISIBLE_TAGS || isExpanded
          ? mappedTags
          : slice(0, NUMBER_OF_VISIBLE_TAGS, mappedTags)}
      </SkeletonLoadingBox>
      <Space horizontal={1} />
      <FilterTagButtons
        tagCount={tags.length}
        collapsedTagCount={tags.length - NUMBER_OF_VISIBLE_TAGS}
        filterPresetsControl={props.filterPresetsControl}
        isExpanded={isExpanded}
        showSaveButton={props.showSaveButton}
        toggleIsExpanded={toggleIsExpanded}
        data-testid={suffixTestId('filterTagButtons', props)}
      />
    </HStack>
  );
}
